import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { RegionService } from 'src/app/services/region.service';
import { DivisionService } from 'src/app/services/division.service';
import { VehicleService } from 'src/app/services/vehicle.service';
import { PaccsbookingService } from 'src/app/services/paccsbooking.service';
import { CashBookingService } from 'src/app/services/cashbooking.service';
import { DepartmentType } from 'src/enum/departmenttype';
import { LookUpResponse } from 'src/app/models/lookup';
import { DbtDistrictWiseFlowProgressReportPopupComponent } from '../dbtdistrictwiseflowprogressreportpopup/dbtdistrictwiseflowprogressreportpopup.component';

// Filter screen for ReportType.DBTDistrictWiseFlowProgressReport (67) - a per-district
// funnel of every stage in the new 11-step DBT subsidy flow (target vs. actual, pending
// vs. completed per stage, additional subsidy, rejected/not-selected/withdrawn). Mirrors
// lottery-report.component.ts's shape (financial year + department mandatory, GO/scheme/
// component/community optional "-- All ... --"), since that's the closest, most recently
// added sibling using the direct RDLC-popup pattern rather than the generic
// ShowDailyReportComponent dialog.
@Component({
  selector: 'app-dbtdistrictwiseflowprogressreport',
  templateUrl: './dbtdistrictwiseflowprogressreport.component.html',
  styleUrls: ['./dbtdistrictwiseflowprogressreport.component.scss']
})
export class DbtDistrictWiseFlowProgressReportComponent implements OnInit {

  form: FormGroup;

  financialYearList: LookUpResponse[] = [];
  filterFinancialYearList: LookUpResponse[] = [];

  departmentList: LookUpResponse[] = [];
  filterDepartmentList: LookUpResponse[] = [];

  goList: LookUpResponse[] = [];
  filterGoList: LookUpResponse[] = [];

  schemeList: LookUpResponse[] = [];
  filterSchemeList: LookUpResponse[] = [];

  componentList: LookUpResponse[] = [];
  filterComponentList: LookUpResponse[] = [];

  communityList: LookUpResponse[] = [];
  filterCommunityList: LookUpResponse[] = [];

  constructor(
    private formBuilder: FormBuilder,
    private regionService: RegionService,
    private divisionService: DivisionService,
    private vehicleService: VehicleService,
    private paccsbookingService: PaccsbookingService,
    private cashbookingService: CashBookingService,
    public tripDialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.initializeForm();
    this.getFinancialYear(true);
    this.getCommunity(true);
  }

  initializeForm(): void {
    this.form = this.formBuilder.group({
      financialYearId: [null, Validators.required],
      departmentId: [null, Validators.required],
      goId: [0],
      schemeId: [0],
      componentId: [0],
      community: [0]
    });
  }

  getFinancialYear(refresh: boolean): void {
    this.regionService.getFinYear(refresh).subscribe(result => {
      if (!result) {
        return;
      }
      this.financialYearList = result;
      this.filterFinancialYearList = this.financialYearList.slice();

      const today = new Date();
      const currentYear = today.getFullYear();
      const currentMonth = today.getMonth() + 1;

      let selectedYear: any;
      if (currentMonth >= 4) {
        selectedYear = this.financialYearList.find((x: any) =>
          Number(x.startYear) === currentYear && Number(x.endYear) === currentYear + 1);
      } else {
        selectedYear = this.financialYearList.find((x: any) =>
          Number(x.startYear) === currentYear - 1 && Number(x.endYear) === currentYear);
      }
      if (!selectedYear && this.financialYearList.length > 0) {
        selectedYear = this.financialYearList[this.financialYearList.length - 1];
      }
      if (selectedYear) {
        this.form.controls['financialYearId'].setValue(selectedYear.key);
      }
      this.getDepartments(true);
    });
  }

  onFinancialYearChange(): void {
    this.departmentList = [];
    this.filterDepartmentList = [];
    this.form.controls['departmentId'].setValue(null);
    this.getDepartments(true);
  }

  getDepartments(refresh: boolean): void {
    this.vehicleService.getDepartmentLookupData(refresh).subscribe(result => {
      this.departmentList = result || [];
      this.filterDepartmentList = this.departmentList.slice();

      const smam = this.filterDepartmentList.find(d => d.key === DepartmentType.SMAM);
      if (smam) {
        this.form.controls['departmentId'].setValue(smam.key);
      } else if (this.filterDepartmentList.length === 1) {
        this.form.controls['departmentId'].setValue(this.filterDepartmentList[0].key);
      }

      this.onDepartmentChange();
    });
  }

  onDepartmentChange(): void {
    this.clearGoSchemeComponent();
    this.goLookup();
  }

  private clearGoSchemeComponent(): void {
    this.goList = [];
    this.filterGoList = [];
    this.form.controls['goId'].setValue(0);
    this.clearSchemeComponent();
  }

  private clearSchemeComponent(): void {
    this.schemeList = [];
    this.filterSchemeList = [];
    this.form.controls['schemeId'].setValue(0);
    this.clearComponent();
  }

  private clearComponent(): void {
    this.componentList = [];
    this.filterComponentList = [];
    this.form.controls['componentId'].setValue(0);
  }

  private goLookup(): void {
    const financialYearId = this.form.value.financialYearId ?? 0;
    const departmentId = this.form.value.departmentId ?? 0;
    this.paccsbookingService.getGoListbyId(financialYearId, departmentId, true).subscribe(result => {
      this.goList = result || [];
      this.filterGoList = this.goList.slice();

      if (this.filterGoList.length === 1) {
        this.form.controls['goId'].setValue(this.filterGoList[0].key);
        this.onGoChange();
      }
    });
  }

  onGoChange(): void {
    this.clearSchemeComponent();
    this.schemeLookup();
  }

  private schemeLookup(): void {
    const goId = this.form.value.goId ?? 0;
    if (!goId) {
      return;
    }
    this.divisionService.getSchemaListbyId(true, goId).subscribe(result => {
      this.schemeList = result || [];
      this.filterSchemeList = this.schemeList.slice();

      if (this.filterSchemeList.length === 1) {
        this.form.controls['schemeId'].setValue(this.filterSchemeList[0].key);
        this.onSchemeChange();
      }
    });
  }

  onSchemeChange(): void {
    this.clearComponent();
    this.componentLookup();
  }

  onComponentChange(): void {
  }

  private componentLookup(): void {
    const goId = this.form.value.goId ?? 0;
    const schemeId = this.form.value.schemeId ?? 0;
    if (!goId || !schemeId) {
      return;
    }
    this.divisionService.getComponetListbyId(goId, schemeId, true).subscribe(result => {
      this.componentList = result || [];
      this.filterComponentList = this.componentList.slice();

      if (this.filterComponentList.length === 1) {
        this.form.controls['componentId'].setValue(this.filterComponentList[0].key);
      }
    });
  }

  getCommunity(refresh: boolean): void {
    this.cashbookingService.getcommunityLookup(refresh).subscribe(result => {
      this.communityList = result || [];
      this.filterCommunityList = this.communityList.slice();
    });
  }

  private validateFormControl(): void {
    Object.keys(this.form.controls).forEach(field => {
      const control = this.form.get(field);
      if (control instanceof FormControl) {
        control.markAsTouched({ onlySelf: true });
      }
    });
  }

  showReport(): void {
    if (!this.form.valid) {
      this.validateFormControl();
      return;
    }

    const financialyearIdvalue = this.filterFinancialYearList.find(
      f => f.key === this.form.value.financialYearId)?.value || '';
    const goIdvalue = this.filterGoList.find(g => g.key === this.form.value.goId)?.value || 'All';
    const schemeIdvalue = this.filterSchemeList.find(s => s.key === this.form.value.schemeId)?.value || 'All';
    const componentIdvalue = this.filterComponentList.find(
      c => c.key === this.form.value.componentId)?.value || 'All';
    const communityvalue = this.filterCommunityList.find(
      c => c.key === this.form.value.community)?.value || 'All';

    this.tripDialog.open(DbtDistrictWiseFlowProgressReportPopupComponent, {
      autoFocus: false,
      disableClose: true,
      width: '95%',
      height: '90%',
      panelClass: 'mat-dialog-report',
      data: {
        financialyearId: this.form.value.financialYearId,
        financialyearIdvalue,
        departmentId: this.form.value.departmentId,
        goId: this.form.value.goId || 0,
        goIdvalue,
        schemeId: this.form.value.schemeId || 0,
        schemeIdvalue,
        componentId: this.form.value.componentId || 0,
        componentIdvalue,
        community: this.form.value.community || 0,
        communityvalue
      }
    });
  }

  clear(): void {
    this.departmentList = [];
    this.filterDepartmentList = [];
    this.goList = [];
    this.filterGoList = [];
    this.schemeList = [];
    this.filterSchemeList = [];
    this.componentList = [];
    this.filterComponentList = [];
    this.initializeForm();
    this.getFinancialYear(true);
  }
}
